import React, {
  useState,
  useEffect,
  useMemo,
  useRef
} from "react";

import axios from "axios";

import {
  Calendar,
  ChevronDown,
  Filter,
  UploadCloud,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Check
} from "lucide-react";

import { API_URL } from "../config";


// ============================================================
// HELPERS (module level, so the dashboard component stays small)
// ============================================================

const LOAD_ERROR_FALLBACK = "Unable to load audit logs.";

// Number from an API statistic; missing / null / 0 all become 0.
const toCount = (value) => Number(value || 0);

// Non-empty values of one field across the received rows.
const valuesOf = (rows, key) => rows.map((row) => row[key]).filter(Boolean);

// Keeps every option discovered so far and adds the new ones, sorted.
const mergeSorted = (previous, additions) =>
  Array.from(new Set([...previous, ...additions])).sort();

// Message shown for a failed audit-log request.
function describeLoadError(err) {
  const status = err.response?.status;

  if (status === 403) {
    return "No permission to view audit logs.";
  }
  if (status === 401) {
    return "Your session has expired. Please log in again.";
  }
  if (err.response) {
    return (
      err.response.data?.message ||
      err.response.data?.error ||
      LOAD_ERROR_FALLBACK
    );
  }
  return "Unable to connect to audit log server.";
}

// Page buttons to show: up to 5 numbers, with "..." gaps for long ranges.
// Every item has its own stable key.
function buildPageItems(page, totalPages) {
  const number = (value) => ({ key: `page-${value}`, value });
  const gapBefore = { key: "gap-before", value: "..." };
  const gapAfter = { key: "gap-after", value: "..." };

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => number(i + 1));
  }
  if (page <= 3) {
    return [number(1), number(2), number(3), gapAfter, number(totalPages)];
  }
  if (page >= totalPages - 2) {
    return [number(1), gapBefore, number(totalPages - 2), number(totalPages - 1), number(totalPages)];
  }
  return [number(1), gapBefore, number(page), gapAfter, number(totalPages)];
}

// First row number shown on the current page (0 when there are no rows).
function showingFrom(rowCount, page, perPage) {
  if (rowCount > 0) {
    return ((page - 1) * perPage) + 1;
  }
  return 0;
}

function badgeClassFor(status) {
  return String(status).toLowerCase() === "success"
    ? "al-badge-success"
    : "al-badge-failed";
}

// One filter button with its drop-down list (used for Actions and Users).
function FilterDropdown({
  id,
  title,
  allLabel,
  emptyLabel,
  selected,
  options,
  openDropdown,
  setOpenDropdown,
  onChange,
  optionStyle
}) {
  return (
    <div className="al-dropdown-wrapper">
      <button
        className={
          `al-toolbar-btn ${
            selected
              ? "active"
              : ""
          }`
        }
        onClick={() => {
          setOpenDropdown(
            openDropdown === id
              ? null
              : id
          );
        }}
      >
        {selected ||
          allLabel}
        <ChevronDown
          size={13}
          className="al-caret"
        />
      </button>

      {openDropdown === id && (
        <div className="al-dropdown">
          <div
            className="al-dropdown-title"
          >
            {title}
          </div>

          <button
            className={
              `al-dropdown-item ${
                selected === ""
                  ? "selected"
                  : ""
              }`
            }
            onClick={() =>
              onChange("")
            }
          >
            <span>
              {allLabel}
            </span>

            {!selected && (
              <Check size={14} />
            )}
          </button>

          {options.length === 0 ? (
            <div
              style={{
                padding: "10px",
                fontSize: "12px",
                color: "#9ca3af"
              }}
            >
              {emptyLabel}
            </div>
          ) : (
            options.map(
              (option) => (
                <button
                  key={option}
                  className={
                    `al-dropdown-item ${
                      selected === option
                        ? "selected"
                        : ""
                    }`
                  }
                  onClick={() =>
                    onChange(
                      option
                    )
                  }
                >
                  <span
                    style={optionStyle}
                  >
                    {option}
                  </span>

                  {selected ===
                    option && (
                    <Check
                      size={14}
                    />
                  )}
                </button>
              )
            )
          )}
        </div>
      )}
    </div>
  );
}

// Rows of the audit table: loading, empty, or the log rows.
function LogsTableBody({ loading, logs }) {
  if (loading) {
    return (
      <tr>
        <td
          colSpan={7}
          className="al-loading"
        >
          Loading audit logs...
        </td>
      </tr>
    );
  }

  if (logs.length === 0) {
    return (
      <tr>
        <td
          colSpan={7}
          style={{
            textAlign:
              "center",
            color:
              "#6b7280",
            padding:
              "24px"
          }}
        >
          No audit logs found.
        </td>
      </tr>
    );
  }

  return logs.map(
    (log, index) => (
      <tr
        key={
          log.id ||
          index
        }
      >
        <td>
          {log.date}
        </td>
        <td>
          {log.user}
        </td>
        <td>
          {log.action}
        </td>
        <td>
          {log.resource}
        </td>
        <td>
          <span
            className={
              `al-badge ${badgeClassFor(log.status)}`
            }
          >
            <span
              className="al-badge-dot"
            />
            {log.status}
          </span>
        </td>
        <td>
          {log.ip}
        </td>
        <td>
          {log.algorithm}
        </td>
      </tr>
    )
  );
}

// Previous / page numbers / Next.
function PaginationControls({ page, totalPages, loading, pageItems, setPage }) {
  return (
    <>
      {/* PREVIOUS */}
      <button
        className="al-page-btn al-page-icon"
        disabled={
          page <= 1 ||
          loading
        }
        onClick={() =>
          setPage(
            Math.max(
              1,
              page - 1
            )
          )
        }
      >
        <ChevronLeft
          size={13}
        />
      </button>

      {/* PAGE NUMBERS */}
      {pageItems.map(
        (item) =>
          item.value === "..." ? (
            <span
              key={item.key}
              style={{
                padding:
                  "0 4px",
                color:
                  "#6b7280"
              }}
            >
              ...
            </span>
          ) : (
            <button
              key={item.key}
              className={
                `al-page-btn ${
                  page === item.value
                    ? "al-active"
                    : ""
                }`
              }
              onClick={() =>
                setPage(item.value)
              }
            >
              {item.value}
            </button>
          )
      )}

      {/* NEXT */}
      <button
        className="al-page-btn al-page-icon"
        disabled={
          page >= totalPages ||
          loading
        }
        onClick={() =>
          setPage(
            Math.min(
              totalPages,
              page + 1
            )
          )
        }
      >
        <ChevronRight
          size={13}
        />
      </button>
    </>
  );
}


export default function AuditLogDashboard() {

  // ============================================================
  // STATE
  // ============================================================

  const [logs, setLogs] = useState([]);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);

  const [totalEvents, setTotalEvents] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const [successfulEvents, setSuccessfulEvents] = useState(0);

  const [failedEvents, setFailedEvents] = useState(0);

  const [activeUsers, setActiveUsers] = useState(0);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [perPage] = useState(10);


  // ============================================================
  // FILTER STATE
  // ============================================================

  const [selectedAction, setSelectedAction] = useState("");

  const [selectedUser, setSelectedUser] = useState("");

  const [selectedStatus, setSelectedStatus] = useState("");

  const [selectedAlgorithm, setSelectedAlgorithm] = useState("");


  // ============================================================
  // DATE FILTER
  // ============================================================

  const [startDate, setStartDate] = useState("");

  const [endDate, setEndDate] = useState("");


  // ============================================================
  // DROPDOWN STATE
  // ============================================================

  const [openDropdown, setOpenDropdown] = useState(null);

  const [showFilterPanel, setShowFilterPanel] = useState(false);


  // ============================================================
  // OPTIONS
  // ============================================================

  const [actionOptions, setActionOptions] = useState([]);

  const [userOptions, setUserOptions] = useState([]);

  const [algorithmOptions, setAlgorithmOptions] = useState([]);


  // ============================================================
  // REFS
  // ============================================================

  const dropdownRef = useRef(null);


  // ============================================================
  // CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  // ============================================================

  useEffect(() => {

    const handleOutsideClick = (event) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {

        setOpenDropdown(null);

      }

    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

    };

  }, []);


  // ============================================================
  // LOAD AUDIT LOGS
  // ============================================================

  const applyStatistics = (statistics) => {

    setTotalEvents(toCount(statistics.total_events));

    setSuccessfulEvents(toCount(statistics.successful_events));

    setFailedEvents(toCount(statistics.failed_events));

    setActiveUsers(toCount(statistics.active_users));

  };

  const applyPagination = (pagination) => {

    const serverPage =
      Number(
        pagination.page || page
      );

    const serverTotalPages =
      Number(
        pagination.total_pages || 1
      );

    setPage(serverPage);

    setTotalPages(
      Math.max(
        1,
        serverTotalPages
      )
    );

  };

  // Keep previously discovered filter values.
  const rememberFilterOptions = (receivedLogs) => {

    setActionOptions(
      (previous) => mergeSorted(previous, valuesOf(receivedLogs, "action"))
    );

    setUserOptions(
      (previous) => mergeSorted(previous, valuesOf(receivedLogs, "user"))
    );

    setAlgorithmOptions(
      (previous) => mergeSorted(previous, valuesOf(receivedLogs, "algorithm"))
    );

  };

  const loadAuditLogs = async () => {

  try {

    setLoading(true);

    setError("");

    const response = await axios.get(
      `${API_URL}/api/audit-logs`,
      {
        params: {
          page: page,
          per_page: perPage,
          search: search.trim(),
          action: selectedAction,
          user: selectedUser,
          status: selectedStatus,
          algorithm: selectedAlgorithm,
          start_date: startDate,
          end_date: endDate
        }
      }
    );

    const result = response.data;

    if (!result.success) {

      setError(
        result.message ||
        LOAD_ERROR_FALLBACK
      );

      setLogs([]);

      return;

    }

    const receivedLogs =
      Array.isArray(result.logs)
        ? result.logs
        : [];

    setLogs(receivedLogs);

    applyStatistics(result.statistics || {});

    applyPagination(result.pagination || {});

    rememberFilterOptions(receivedLogs);

  } catch (err) {

    console.error(
      "Audit log API error:",
      err
    );

    setLogs([]);

    setError(describeLoadError(err));

  } finally {

    setLoading(false);

  }

};


  // ============================================================
  // LOAD WHEN FILTERS CHANGE
  // ============================================================

  useEffect(() => {

    loadAuditLogs();

  }, [
    page,
    search,
    selectedAction,
    selectedUser,
    selectedStatus,
    selectedAlgorithm,
    startDate,
    endDate
  ]);


  // ============================================================
  // SEARCH
  // ============================================================

  const handleSearch = (value) => {

    setSearch(value);

    setPage(1);

  };


  // ============================================================
  // ACTION FILTER
  // ============================================================

  const handleActionChange = (action) => {

    setSelectedAction(action);

    setPage(1);

    setOpenDropdown(null);

  };


  // ============================================================
  // USER FILTER
  // ============================================================

  const handleUserChange = (user) => {

    setSelectedUser(user);

    setPage(1);

    setOpenDropdown(null);

  };


  // ============================================================
  // STATUS FILTER
  // ============================================================

  const handleStatusChange = (status) => {

    setSelectedStatus(status);

    setPage(1);

  };


  // ============================================================
  // ALGORITHM FILTER
  // ============================================================

  const handleAlgorithmChange = (algorithm) => {

    setSelectedAlgorithm(algorithm);

    setPage(1);

  };


  // ============================================================
  // DATE FILTER
  // ============================================================

  const handleStartDateChange = (value) => {

    setStartDate(value);

    setPage(1);

  };


  const handleEndDateChange = (value) => {

    setEndDate(value);

    setPage(1);

  };


  // ============================================================
  // CLEAR FILTERS
  // ============================================================

  const clearFilters = () => {

    setSearch("");

    setSelectedAction("");

    setSelectedUser("");

    setSelectedStatus("");

    setSelectedAlgorithm("");

    setStartDate("");

    setEndDate("");

    setPage(1);

    setOpenDropdown(null);

  };


  // ============================================================
  // ACTIVE FILTER COUNT
  // ============================================================

  const activeFilterCount = useMemo(() => {

    let count = 0;

    if (selectedAction) count++;

    if (selectedUser) count++;

    if (selectedStatus) count++;

    if (selectedAlgorithm) count++;

    if (startDate) count++;

    if (endDate) count++;

    return count;

  }, [
    selectedAction,
    selectedUser,
    selectedStatus,
    selectedAlgorithm,
    startDate,
    endDate
  ]);


  // ============================================================
  // DATE DISPLAY
  // ============================================================

  const dateDisplay = useMemo(() => {

    if (startDate && endDate) {

      return `${formatDate(startDate)} – ${formatDate(endDate)}`;

    }

    if (startDate) {

      return `From ${formatDate(startDate)}`;

    }

    if (endDate) {

      return `Until ${formatDate(endDate)}`;

    }

    return "All Dates";

  }, [
    startDate,
    endDate
  ]);


  // ============================================================
  // PAGE NUMBERS
  // ============================================================

  const pageNumbers = useMemo(
    () => buildPageItems(page, totalPages),
    [
      page,
      totalPages
    ]
  );


  // ============================================================
  // EXPORT CSV
  // ============================================================

  const handleExport = () => {

    if (!logs.length) {

      alert(
        "No audit logs available to export."
      );

      return;

    }


    const headers = [
      "Date & Time",
      "User",
      "Action",
      "Resource",
      "Status",
      "IP Address",
      "Algorithm"
    ];


    const rows = logs.map((log) => [

      log.date,

      log.user,

      log.action,

      log.resource,

      log.status,

      log.ip,

      log.algorithm

    ]);


    const csvContent = [

      headers,

      ...rows

    ]

      .map((row) =>

        row

          .map((value) =>

            `"${String(
              value ?? ""
            ).replace(
              /"/g,
              '""'
            )}"`

          )

          .join(",")

      )

      .join("\n");


    const blob = new Blob(
      [csvContent],
      {
        type:
          "text/csv;charset=utf-8;"
      }
    );


    const url =
      URL.createObjectURL(blob);


    const link =
      document.createElement("a");


    link.href = url;


    link.download =
      `audit_log_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;


    document.body.appendChild(link);


    link.click();


    document.body.removeChild(link);


    URL.revokeObjectURL(url);

  };


  // ============================================================
  // RENDER
  // ============================================================

  return (

    <div className="al-root">

      <style>{`

        * {
          box-sizing: border-box;
        }

        .al-root {
          background: #ffffff;
          color: #1a1d29;
          font-family:
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Inter,
            Roboto,
            sans-serif;

          width: calc(100% + 48px);
          margin: -24px;

          min-height:
            calc(100% + 48px);
        }


        /* =====================================================
           TOOLBAR
        ===================================================== */

        .al-toolbar {

          display: flex;

          gap: 10px;

          flex-wrap: wrap;

          align-items: center;

          padding: 14px 20px;

          border-bottom:
            1px solid #e7e8ec;

          background: #fff;

          position: relative;

          z-index: 20;

        }


        .al-toolbar-btn {

          display: flex;

          align-items: center;

          gap: 7px;

          background: #fff;

          border:
            1px solid #e7e8ec;

          border-radius: 9px;

          padding: 8px 13px;

          font-size: 13px;

          color: #1a1d29;

          cursor: pointer;

          white-space: nowrap;

          transition: 0.15s;

        }


        .al-toolbar-btn:hover {

          border-color:
            #c5c7d0;

          background: #fafafa;

        }


        .al-toolbar-btn.active {

          border-color:
            #5b4bfb;

          color:
            #5b4bfb;

          background:
            #f7f5ff;

        }


        .al-toolbar-btn svg {

          opacity: 0.6;

          flex-shrink: 0;

        }


        .al-toolbar-btn .al-caret {

          margin-left: 4px;

          opacity: 0.45;

        }


        .al-push-right {

          margin-left: auto;

        }


        /* =====================================================
           DROPDOWN
        ===================================================== */

        .al-dropdown-wrapper {

          position: relative;

        }


        .al-dropdown {

          position: absolute;

          top: calc(100% + 7px);

          left: 0;

          min-width: 210px;

          background: #fff;

          border:
            1px solid #e7e8ec;

          border-radius: 10px;

          box-shadow:
            0 12px 30px
            rgba(0,0,0,0.10);

          padding: 6px;

          z-index: 100;

        }


        .al-dropdown-right {

          right: 0;

          left: auto;

        }


        .al-dropdown-title {

          font-size: 11px;

          font-weight: 700;

          color: #9ca3af;

          padding:
            8px 10px 5px;

          text-transform:
            uppercase;

          letter-spacing:
            0.04em;

        }


        .al-dropdown-item {

          width: 100%;

          border: none;

          background: transparent;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 12px;

          padding:
            9px 10px;

          border-radius: 7px;

          cursor: pointer;

          font-size: 13px;

          color: #374151;

          text-align: left;

        }


        .al-dropdown-item:hover {

          background:
            #f5f5f8;

        }


        .al-dropdown-item.selected {

          background:
            #f0edff;

          color:
            #5b4bfb;

          font-weight: 600;

        }


        /* =====================================================
           DATE PANEL
        ===================================================== */

        .al-date-panel {

          min-width: 290px;

          padding: 12px;

        }


        .al-date-field {

          margin-bottom: 12px;

        }


        .al-date-label {

          display: block;

          font-size: 11px;

          font-weight: 600;

          color: #6b7280;

          margin-bottom: 5px;

        }


        .al-date-input {

          width: 100%;

          border:
            1px solid #e5e7eb;

          border-radius: 7px;

          padding:
            8px 9px;

          font-size: 13px;

          outline: none;

        }


        .al-date-input:focus {

          border-color:
            #5b4bfb;

        }


        .al-date-actions {

          display: flex;

          justify-content:
            flex-end;

          gap: 7px;

          padding-top: 5px;

        }


        .al-small-btn {

          border:
            1px solid #e5e7eb;

          background: #fff;

          border-radius: 7px;

          padding:
            7px 11px;

          cursor: pointer;

          font-size: 12px;

        }


        .al-small-btn.primary {

          background:
            #5b4bfb;

          color: #fff;

          border-color:
            #5b4bfb;

        }


        /* =====================================================
           FILTER PANEL
        ===================================================== */

        .al-filter-panel {

          position: absolute;

          top: calc(100% + 8px);

          left: 230px;

          width: 330px;

          background: #fff;

          border:
            1px solid #e7e8ec;

          border-radius: 12px;

          box-shadow:
            0 15px 35px
            rgba(0,0,0,0.12);

          padding: 16px;

          z-index: 100;

        }


        .al-filter-header {

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          margin-bottom: 14px;

        }


        .al-filter-title {

          font-size: 14px;

          font-weight: 700;

        }


        .al-close-btn {

          width: 27px;

          height: 27px;

          border: none;

          background:
            #f5f5f7;

          border-radius: 7px;

          display: flex;

          align-items: center;

          justify-content: center;

          cursor: pointer;

        }


        .al-filter-group {

          margin-bottom: 14px;

        }


        .al-filter-label {

          display: block;

          font-size: 11px;

          font-weight: 600;

          color: #6b7280;

          margin-bottom: 6px;

        }


        .al-select {

          width: 100%;

          border:
            1px solid #e5e7eb;

          border-radius: 8px;

          padding:
            9px 10px;

          background: #fff;

          font-size: 13px;

          outline: none;

          color: #374151;

        }


        .al-select:focus {

          border-color:
            #5b4bfb;

        }


        .al-filter-footer {

          display: flex;

          justify-content:
            space-between;

          align-items: center;

          border-top:
            1px solid #f0f0f3;

          padding-top: 12px;

        }


        .al-clear-filter {

          border: none;

          background: transparent;

          color: #dc2626;

          font-size: 12px;

          cursor: pointer;

          font-weight: 600;

        }


        .al-apply-filter {

          border: none;

          background:
            #5b4bfb;

          color: #fff;

          border-radius: 7px;

          padding:
            8px 13px;

          font-size: 12px;

          cursor: pointer;

          font-weight: 600;

        }


        /* =====================================================
           STATS
        ===================================================== */

        .al-stats-row {

          display: grid;

          grid-template-columns:
            repeat(4, 1fr);

          gap: 16px;

          padding: 16px 20px;

          background: #fff;

          border-bottom:
            1px solid #e7e8ec;

        }


        .al-stat-card {

          background: #fff;

          border:
            1px solid #e7e8ec;

          border-radius: 12px;

          padding:
            18px 20px;

        }


        .al-stat-label {

          font-size: 12px;

          color: #6b7280;

          margin-bottom: 6px;

          font-weight: 500;

        }


        .al-stat-value {

          font-size: 28px;

          font-weight: 700;

          letter-spacing:
            -0.02em;

          color: #1a1d29;

          line-height: 1;

        }


        .al-stat-sub {

          font-size: 12px;

          margin-top: 6px;

          font-weight: 600;

        }


        .al-sub-up {

          color: #16a34a;

        }


        .al-sub-down {

          color: #dc2626;

        }


        .al-sub-neutral {

          color: #6b7280;

          font-weight: 400;

        }


        /* =====================================================
           PANEL
        ===================================================== */

        .al-panel {

          background: #fff;

          padding:
            20px 24px;

        }


        .al-panel-header {

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          margin-bottom: 14px;

        }


        .al-panel-title {

          font-size: 15px;

          font-weight: 700;

          color: #1a1d29;

        }


        .al-export-btn {

          display: flex;

          align-items: center;

          gap: 7px;

          background:
            #5b4bfb;

          color: #fff;

          border: none;

          border-radius: 9px;

          padding:
            8px 14px;

          font-size: 13px;

          font-weight: 600;

          cursor: pointer;

        }


        .al-export-btn:hover {

          background:
            #4a3ce0;

        }


        /* =====================================================
           SEARCH
        ===================================================== */

        .al-search-box {

          display: flex;

          align-items: center;

          gap: 8px;

          border:
            1px solid #e7e8ec;

          border-radius: 9px;

          padding:
            8px 12px;

          margin-bottom: 14px;

        }


        .al-search-box:focus-within {

          border-color:
            #5b4bfb;

          box-shadow:
            0 0 0 2px
            rgba(91,75,251,0.08);

        }


        .al-search-box input {

          border: none;

          outline: none;

          font-size: 13px;

          width: 100%;

          background: transparent;

          color: #1a1d29;

        }


        .al-search-box svg {

          opacity: 0.45;

          flex-shrink: 0;

        }


        /* =====================================================
           ACTIVE FILTER CHIPS
        ===================================================== */

        .al-filter-chips {

          display: flex;

          flex-wrap: wrap;

          gap: 7px;

          margin-bottom: 14px;

        }


        .al-filter-chip {

          display: inline-flex;

          align-items: center;

          gap: 5px;

          background:
            #f0edff;

          color:
            #5b4bfb;

          border-radius: 999px;

          padding:
            5px 9px;

          font-size: 11px;

          font-weight: 600;

        }


        .al-chip-remove {

          border: none;

          background: transparent;

          padding: 0;

          cursor: pointer;

          display: flex;

          align-items: center;

          color:
            #5b4bfb;

        }


        /* =====================================================
           TABLE
        ===================================================== */

        .al-table-wrapper {

          width: 100%;

          overflow-x: auto;

        }


        .al-table {

          width: 100%;

          border-collapse:
            collapse;

          font-size: 13px;

        }


        .al-table thead th {

          text-align: left;

          font-weight: 600;

          color: #6b7280;

          font-size: 12px;

          padding:
            8px 12px;

          border-bottom:
            1px solid #e7e8ec;

          background: #fafafa;

        }


        .al-table tbody td {

          padding:
            11px 12px;

          border-bottom:
            1px solid #f0f0f3;

          color: #374151;

          white-space:
            nowrap;

        }


        .al-table tbody tr:last-child td {

          border-bottom: none;

        }


        .al-table tbody tr:hover {

          background:
            #fafafc;

        }


        /* =====================================================
           STATUS
        ===================================================== */

        .al-badge {

          display: inline-flex;

          align-items: center;

          gap: 5px;

          padding:
            3px 10px;

          border-radius:
            999px;

          font-size: 12px;

          font-weight: 600;

          white-space:
            nowrap;

        }


        .al-badge-dot {

          width: 6px;

          height: 6px;

          border-radius: 50%;

          display: inline-block;

          flex-shrink: 0;

        }


        .al-badge-success {

          background:
            #dcfce7;

          color:
            #16a34a;

        }


        .al-badge-success
        .al-badge-dot {

          background:
            #16a34a;

        }


        .al-badge-failed {

          background:
            #fee2e2;

          color:
            #dc2626;

        }


        .al-badge-failed
        .al-badge-dot {

          background:
            #dc2626;

        }


        /* =====================================================
           FOOTER
        ===================================================== */

        .al-footer-row {

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          margin-top: 14px;

          font-size: 13px;

          color: #6b7280;

        }


        .al-pagination {

          display: flex;

          gap: 5px;

          align-items: center;

        }


        .al-page-btn {

          min-width: 30px;

          height: 30px;

          border-radius: 7px;

          border:
            1px solid #e7e8ec;

          background: #fff;

          font-size: 13px;

          cursor: pointer;

          display: flex;

          align-items: center;

          justify-content:
            center;

          color: #1a1d29;

        }


        .al-page-btn.al-active {

          background:
            #5b4bfb;

          color: #fff;

          border-color:
            #5b4bfb;

        }


        .al-page-btn:hover:not(
          .al-active
        ) {

          border-color:
            #c5c7d0;

        }


        .al-page-btn:disabled {

          opacity: 0.4;

          cursor:
            not-allowed;

        }


        .al-page-icon {

          color:
            #6b7280;

        }


        /* =====================================================
           LOADING
        ===================================================== */

        .al-loading {

          text-align: center;

          padding: 40px;

          color:
            #6b7280;

        }


        .al-error {

          padding:
            14px 16px;

          margin-bottom:
            14px;

          border-radius: 9px;

          background:
            #fee2e2;

          color:
            #b91c1c;

          font-size: 13px;

        }


        .al-refresh-btn {

          display: flex;

          align-items: center;

          gap: 7px;

          background: #fff;

          color: #1a1d29;

          border:
            1px solid #e7e8ec;

          border-radius: 9px;

          padding:
            8px 13px;

          font-size: 13px;

          cursor: pointer;

        }


        .al-refresh-btn:hover {

          border-color:
            #c5c7d0;

        }


        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 900px) {

          .al-stats-row {

            grid-template-columns:
              repeat(2, 1fr);

          }

          .al-filter-panel {

            left: 10px;

          }

        }


        @media (max-width: 600px) {

          .al-stats-row {

            grid-template-columns:
              1fr;

          }

          .al-footer-row {

            flex-direction:
              column;

            gap: 12px;

            align-items:
              flex-start;

          }

          .al-push-right {

            margin-left: 0;

          }

          .al-filter-panel {

            left: 0;

            width:
              calc(100vw - 30px);

          }

        }


        @keyframes spin {

          from {
            transform:
              rotate(0deg);
          }

          to {
            transform:
              rotate(360deg);
          }

        }

      `}</style>


      {/* ========================================================
          TOOLBAR
      ======================================================== */}

      <div
        className="al-toolbar"
        ref={dropdownRef}
      >


        {/* ======================================================
            DATE
        ====================================================== */}

        <div className="al-dropdown-wrapper">

          <button
            className={
              `al-toolbar-btn ${
                startDate || endDate
                  ? "active"
                  : ""
              }`
            }

            onClick={() => {

              setOpenDropdown(
                openDropdown === "date"
                  ? null
                  : "date"
              );

            }}
          >

            <Calendar size={14} />

            {dateDisplay}

            <ChevronDown
              size={13}
              className="al-caret"
            />

          </button>


          {openDropdown === "date" && (

            <div className="al-dropdown">

              <div className="al-date-panel">

                <div
                  className="al-date-field"
                >

                  <label
                    className="al-date-label"
                  >
                    From
                  </label>

                  <input
                    type="date"
                    className="al-date-input"
                    value={startDate}
                    onChange={(e) =>
                      handleStartDateChange(
                        e.target.value
                      )
                    }
                  />

                </div>


                <div
                  className="al-date-field"
                >

                  <label
                    className="al-date-label"
                  >
                    To
                  </label>

                  <input
                    type="date"
                    className="al-date-input"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) =>
                      handleEndDateChange(
                        e.target.value
                      )
                    }
                  />

                </div>


                <div
                  className="al-date-actions"
                >

                  <button
                    className="al-small-btn"
                    onClick={() => {

                      setStartDate("");

                      setEndDate("");

                      setPage(1);

                      setOpenDropdown(
                        null
                      );

                    }}
                  >
                    Clear
                  </button>


                  <button
                    className="al-small-btn primary"
                    onClick={() =>
                      setOpenDropdown(
                        null
                      )
                    }
                  >
                    Apply
                  </button>

                </div>

              </div>

            </div>

          )}

        </div>


        {/* ======================================================
            ACTIONS
        ====================================================== */}

        <FilterDropdown
          id="action"
          title="Actions"
          allLabel="All Actions"
          emptyLabel="No actions available"
          selected={selectedAction}
          options={actionOptions}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          onChange={handleActionChange}
        />


        {/* ======================================================
            USERS
        ====================================================== */}

        <FilterDropdown
          id="user"
          title="Users"
          allLabel="All Users"
          emptyLabel="No users available"
          selected={selectedUser}
          options={userOptions}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          onChange={handleUserChange}
          optionStyle={{
            maxWidth: "170px",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        />


        {/* ======================================================
            FILTERS
        ====================================================== */}

        <button
          className={
            `al-toolbar-btn ${
              activeFilterCount > 0
                ? "active"
                : ""
            }`
          }

          onClick={() =>
            setShowFilterPanel(
              !showFilterPanel
            )
          }
        >

          <Filter size={13} />

          Filters

          {activeFilterCount > 0 && (

            <span
              style={{
                background: "#5b4bfb",
                color: "#fff",
                borderRadius: "999px",
                minWidth: "18px",
                height: "18px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: 700
              }}
            >
              {activeFilterCount}
            </span>

          )}

        </button>


        {/* ======================================================
            FILTER PANEL
        ====================================================== */}

        {showFilterPanel && (

          <div
            className="al-filter-panel"
            ref={dropdownRef}
          >

            <div
              className="al-filter-header"
            >

              <div
                className="al-filter-title"
              >
                Advanced Filters
              </div>


              <button
                className="al-close-btn"
                onClick={() =>
                  setShowFilterPanel(
                    false
                  )
                }
              >

                <X size={15} />

              </button>

            </div>


            {/* STATUS */}

            <div
              className="al-filter-group"
            >

              <label
                className="al-filter-label"
              >
                Status
              </label>


              <select
                className="al-select"
                value={selectedStatus}
                onChange={(e) =>
                  handleStatusChange(
                    e.target.value
                  )
                }
              >

                <option value="">
                  All Statuses
                </option>

                <option value="Success">
                  Success
                </option>

                <option value="Failed">
                  Failed
                </option>

              </select>

            </div>


            {/* ALGORITHM */}

            <div
              className="al-filter-group"
            >

              <label
                className="al-filter-label"
              >
                Algorithm
              </label>


              <select
                className="al-select"
                value={selectedAlgorithm}
                onChange={(e) =>
                  handleAlgorithmChange(
                    e.target.value
                  )
                }
              >

                <option value="">
                  All Algorithms
                </option>


                {algorithmOptions.map(
                  (algorithm) => (

                    <option
                      key={algorithm}
                      value={algorithm}
                    >
                      {algorithm}
                    </option>

                  )
                )}

              </select>

            </div>


            <div
              className="al-filter-footer"
            >

              <button
                className="al-clear-filter"
                onClick={() => {

                  setSelectedStatus("");

                  setSelectedAlgorithm("");

                  setSelectedAction("");

                  setSelectedUser("");

                  setStartDate("");

                  setEndDate("");

                  setPage(1);

                }}
              >

                Clear all

              </button>


              <button
                className="al-apply-filter"
                onClick={() =>
                  setShowFilterPanel(
                    false
                  )
                }
              >

                Apply Filters

              </button>

            </div>

          </div>

        )}


        {/* ======================================================
            REFRESH
        ====================================================== */}

        <button
          className="al-refresh-btn al-push-right"
          onClick={loadAuditLogs}
          disabled={loading}
        >

          <RefreshCw
            size={13}
            style={{
              animation:
                loading
                  ? "spin 1s linear infinite"
                  : "none"
            }}
          />

          Refresh

        </button>

      </div>


      {/* ========================================================
          STATISTICS
      ======================================================== */}

      <div className="al-stats-row">

        <div className="al-stat-card">

          <div className="al-stat-label">
            Total Events
          </div>

          <div className="al-stat-value">
            {totalEvents.toLocaleString()}
          </div>

        </div>


        <div className="al-stat-card">

          <div className="al-stat-label">
            Successful Events
          </div>

          <div className="al-stat-value">
            {successfulEvents.toLocaleString()}
          </div>

          <div
            className="al-stat-sub al-sub-up"
          >

            {totalEvents > 0
              ? `${(
                  successfulEvents /
                  totalEvents *
                  100
                ).toFixed(1)}%`
              : "0%"}

          </div>

        </div>


        <div className="al-stat-card">

          <div className="al-stat-label">
            Failed Events
          </div>

          <div className="al-stat-value">
            {failedEvents.toLocaleString()}
          </div>

          <div
            className="al-stat-sub al-sub-down"
          >

            {totalEvents > 0
              ? `${(
                  failedEvents /
                  totalEvents *
                  100
                ).toFixed(1)}%`
              : "0%"}

          </div>

        </div>


        <div className="al-stat-card">

          <div className="al-stat-label">
            Active Users
          </div>

          <div className="al-stat-value">
            {activeUsers.toLocaleString()}
          </div>

          <div
            className="al-stat-sub al-sub-neutral"
          >
            From audit activity
          </div>

        </div>

      </div>


      {/* ========================================================
          TABLE PANEL
      ======================================================== */}

      <div className="al-panel">

        <div
          className="al-panel-header"
        >

          <div
            className="al-panel-title"
          >
            Audit Logs
          </div>


          <button
            className="al-export-btn"
            onClick={handleExport}
          >

            <UploadCloud size={14} />

            Export

          </button>

        </div>


        {/* ======================================================
            SEARCH
        ====================================================== */}

        <div
          className="al-search-box"
        >

          <Search size={14} />

          <input
            placeholder="Search logs..."
            value={search}
            onChange={(e) =>
              handleSearch(
                e.target.value
              )
            }
          />

        </div>


        {/* ======================================================
            ACTIVE FILTER CHIPS
        ====================================================== */}

        {activeFilterCount > 0 && (

          <div
            className="al-filter-chips"
          >

            {startDate && (

              <div
                className="al-filter-chip"
              >

                From:
                {formatDate(startDate)}

                <button
                  className="al-chip-remove"
                  onClick={() => {

                    setStartDate("");

                    setPage(1);

                  }}
                >

                  <X size={11} />

                </button>

              </div>

            )}


            {endDate && (

              <div
                className="al-filter-chip"
              >

                To:
                {formatDate(endDate)}

                <button
                  className="al-chip-remove"
                  onClick={() => {

                    setEndDate("");

                    setPage(1);

                  }}
                >

                  <X size={11} />

                </button>

              </div>

            )}


            {selectedAction && (

              <div
                className="al-filter-chip"
              >

                Action:
                {selectedAction}

                <button
                  className="al-chip-remove"
                  onClick={() =>
                    handleActionChange("")
                  }
                >

                  <X size={11} />

                </button>

              </div>

            )}


            {selectedUser && (

              <div
                className="al-filter-chip"
              >

                User:
                {selectedUser}

                <button
                  className="al-chip-remove"
                  onClick={() =>
                    handleUserChange("")
                  }
                >

                  <X size={11} />

                </button>

              </div>

            )}


            {selectedStatus && (

              <div
                className="al-filter-chip"
              >

                Status:
                {selectedStatus}

                <button
                  className="al-chip-remove"
                  onClick={() => {

                    setSelectedStatus("");

                    setPage(1);

                  }}
                >

                  <X size={11} />

                </button>

              </div>

            )}


            {selectedAlgorithm && (

              <div
                className="al-filter-chip"
              >

                Algorithm:
                {selectedAlgorithm}

                <button
                  className="al-chip-remove"
                  onClick={() => {

                    setSelectedAlgorithm("");

                    setPage(1);

                  }}
                >

                  <X size={11} />

                </button>

              </div>

            )}


            <button
              className="al-clear-filter"
              onClick={clearFilters}
              style={{
                marginLeft: "3px"
              }}
            >

              Clear all

            </button>

          </div>

        )}


        {/* ======================================================
            ERROR
        ====================================================== */}

        {error && (

          <div className="al-error">
            {error}
          </div>

        )}


        {/* ======================================================
            TABLE
        ====================================================== */}

        <div
          className="al-table-wrapper"
        >

          <table className="al-table">

            <thead>

              <tr>

                <th>
                  Date &amp; Time
                </th>

                <th>
                  User
                </th>

                <th>
                  Action
                </th>

                <th>
                  Resource
                </th>

                <th>
                  Status
                </th>

                <th>
                  IP Address
                </th>

                <th>
                  Algorithm
                </th>

              </tr>

            </thead>


            <tbody>

              <LogsTableBody loading={loading} logs={logs} />

            </tbody>

          </table>

        </div>


        {/* ======================================================
            FOOTER
        ====================================================== */}

        <div
          className="al-footer-row"
        >

          <div>

            Showing{" "}
            {showingFrom(logs.length, page, perPage)}
            {" "}to{" "}

            {(
              (page - 1) *
                perPage
            ) + logs.length}

            {" "}of{" "}

            {totalEvents.toLocaleString()}

            {" "}events

          </div>


          <div
            className="al-pagination"
          >

            <PaginationControls
              page={page}
              totalPages={totalPages}
              loading={loading}
              pageItems={pageNumbers}
              setPage={setPage}
            />

          </div>

        </div>

      </div>

    </div>

  );

}


// ============================================================
// DATE FORMATTER
// ============================================================

function formatDate(value) {

  if (!value) {
    return "";
  }


  const date =
    new Date(
      `${value}T00:00:00`
    );


  if (Number.isNaN(
    date.getTime()
  )) {

    return value;

  }


  return date.toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric"
    }
  );

}